import React, { useCallback, useEffect, useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { withStyles } from '@material-ui/styles';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import orderBy from 'lodash.orderby';

import { Tab, Tabs } from '@material-ui/core';
import styles from '../styles/informationStep';
import { actions as reduxActions } from '../reducers';
import { getData } from '../../../utils/http';
import { OUTSIDE_API } from '../../../consts/endpoints';
import validateSchema from '../actions/validateSchema';
import TabPanel from '../../../partials/TabPanel';
import Optional from '../../../utils/optional';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import MandatoryField from './MandatoryField';
import GeneralTab from './GeneralTab';
import AdditionalTab from './AdditionalTab';
import mapDefaultAddress from '../actions/mapDefaultAddress';
import {
  isArray,
  isDefined,
  isObject,
  isUndefined
} from '../../../utils/isDefined';
import { translate } from '../../../i18n/I18nProvider';
import Groups from '../../editUser/components/Groups';

const InformationStep = ({
  classes,
  resestUserWizard,
  handleNext,
  setUserData,
  firstStepData,
  selectOptions,
  users,
  connect30_domain,
  setMobileSubscriptions,
  onFirstStepDirtyChange,
  onCloseAttempt,
  allGroupsOptions
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const onSubmit = useCallback(
    async values => {
      let stepSize = 1;

      const fifthStep = mapDefaultAddress(values);

      handleNext(stepSize);
      setUserData({ ...values }, 'firstStep');
      setMobileSubscriptions(null);
      setUserData(fifthStep, 'fifthStep');
    },
    // eslint-disable-next-line
    []
  );

  return (
    <Formik
      initialValues={{
        ipad_support: false,
        premium_intrusion: false,
        premium_attendant: false,
        call_control_api: false,
        cloud_cti_api: false,
        line_state_api: false,
        crm_connect: false,
        unified_inbox: false,
        cloud_crm_connector: false,
        chat: false,
        teams_application: false,
        teams_telephony_go: false,
        teams_telephony: false,
        ...firstStepData
      }}
      enableReinitialize
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={onSubmit}
      validationSchema={validateSchema({ users, connect30_domain }).infoStep}
    >
      {({
        handleSubmit,
        errors,
        values,
        handleBlur,
        handleChange,
        setFieldValue,
        dirty
      }) => {
        const fieldProps = {
          errors: {
            ...errors,
            mobile_number: errors.mobile_number
          },
          values,
          handleBlur,
          handleChange,
          setFieldValue
        };

        if (values.isNewNumber) {
          values.mobile_number = '';
        }

        const onCopyFromUserChange = async selectedValue => {
          setFieldValue('copy_from_user', selectedValue);
          setFieldValue('copiedUserDetails', undefined);

          const selectedUser = Optional(selectOptions.usersSelectOptions)
            .map(options =>
              options.find(option => option.value === selectedValue)
            )
            .or(undefined);

          if (!isDefined(selectedUser)) {
            setFieldValue('cost_center', '');
            setFieldValue('ipad_support', false);
            setFieldValue('premium_attendant', false);
            setFieldValue('call_control_api', false);
            setFieldValue('cloud_cti_api', false);
            setFieldValue('line_state_api', false);
            setFieldValue('crm_connect', false);
            setFieldValue('unified_inbox', false);
            setFieldValue('cloud_crm_connector', false);
            setFieldValue('chat', false);
            setFieldValue('teams_application', false);
            setFieldValue('teams_telephony_go', false);
            setFieldValue('teams_telephony', false);
            setFieldValue('call_recording', false);
            return;
          }
          setFieldValue(
            'cost_center',
            Optional(selectedUser.cost_center).or('')
          );
          setFieldValue('department', Optional(selectedUser.department).or(''));

          const userId = selectedUser?.user_id;
          setFieldValue('copied_user_id', userId);

          if (isDefined(userId) && isDefined(connect30_domain)) {
            const userResponse = await getData(
              `${OUTSIDE_API.CLIENTS}/${connect30_domain}/users/${userId}`
            );
            setFieldValue('copiedUserDetails', Optional(userResponse).or({}));
            const u = Optional(userResponse).or({});
            setFieldValue('ipad_support', Boolean(u.ipad_support));
            setFieldValue('premium_attendant', Boolean(u.premium_attendant));
            setFieldValue('call_control_api', Boolean(u.call_control_api));
            setFieldValue('cloud_cti_api', Boolean(u.cloud_cti_api));
            setFieldValue('line_state_api', Boolean(u.line_state_api));
            setFieldValue('crm_connect', Boolean(u.crm_connect));
            setFieldValue('unified_inbox', Boolean(u.unified_inbox));
            setFieldValue(
              'cloud_crm_connector',
              Boolean(u.cloud_crm_connector)
            );
            setFieldValue('chat', Boolean(u.chat));
            setFieldValue('teams_application', Boolean(u.teams_application));
            setFieldValue('teams_telephony_go', Boolean(u.teams_telephony_go));
            setFieldValue('teams_telephony', Boolean(u.teams_telephony));
            setFieldValue('groups', Optional(u.user_groups).or([]));
            setFieldValue('language', Optional(u.language).or(''));
            setFieldValue('exchange_sync', Optional(u.exchange_sync).or(false));
            setFieldValue('fields', mapFieldsFromSelectedUser(u));
            setFieldValue('call_recording', Boolean(u.call_recording));
          }
        };

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (Object.keys(errors).length > 0) {
            setActiveTab(0);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [errors]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (dirty && onFirstStepDirtyChange) {
            onFirstStepDirtyChange(true);
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [dirty]);

        return (
          <Form onSubmit={handleSubmit}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              className={classes.tabs}
            >
              <Tab label={translate('GENERAL')} />
              <Tab
                label={translate('ADDITIONAL')}
                disabled={!values.copy_from_user}
              />
              <Tab
                label={translate('GROUPS')}
                disabled={!values.copy_from_user}
              />
            </Tabs>
            <div className={classes.tabContent}>
              <TabPanel value={activeTab} index={0}>
                <GeneralTab
                  fieldProps={fieldProps}
                  setFieldValue={setFieldValue}
                  selectOptions={selectOptions}
                  onCopyFromUserChange={onCopyFromUserChange}
                  connect30_domain={connect30_domain}
                />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <AdditionalTab fieldProps={fieldProps} />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <FieldArray
                  name="groups"
                  render={arrayHelpers => (
                    <Groups
                      options={allGroupsOptions}
                      arrayHelpers={arrayHelpers}
                    />
                  )}
                />
              </TabPanel>
            </div>
            <MandatoryField />
            <div className={classes.autoCreatedInfo}>
              {translate('REQUEST_NEW_USER.AUTO_CREATED_INFO')}
            </div>

            <div className={classes.footer}>
              <PrimaryButton onClick={handleSubmit}>
                {translate('BUTTON.NEXT')}
              </PrimaryButton>
              <SecondaryButton onClick={onCloseAttempt || resestUserWizard}>
                {translate('BUTTON.CANCEL')}
              </SecondaryButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapStatesToProps = ({
  requestUserWizard,
  telephonyUsers,
  selectClient,
  auth
}) => {
  return {
    firstStepData: requestUserWizard.userData.firstStep,
    selectOptions: requestUserWizard.selectOptions,
    users: telephonyUsers.users,
    connect30_domain: Optional(selectClient.selectedClient.connect30_domain).or(
      auth.defaultClient.connect30_domain
    ),
    allGroupsOptions: requestUserWizard.selectOptions.allGroupsOptions
  };
};

const mapDispatchToProps = {
  ...reduxActions
};

export default flowRight(
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(InformationStep);

const mapFieldsFromSelectedUser = (selectedUser = {}) => {
  const userFields = Optional(selectedUser?.dir_fields).or([]);

  const orderedFields = orderBy(userFields, 'id');

  return orderedFields.map(field => {
    const { value, label, id } = field;

    return {
      ...field,
      value: Optional(value).or(''),
      name: Optional(label).or(id)
    };
  });
};
